import type { HttpResponse } from '@asset-metrix/portal-gateway-client-js';
import {
  createEventSource,
  type EventSourceClient,
  type FetchLike,
} from 'eventsource-client';
import { z } from 'zod';

import { extractErrorMessage } from '../../utils/errorMessage';
import { HttpError } from '../errors';
import { fetchWithSessionRefresh } from '../portalGatewayClient';

const KNOWN_EVENT_TYPES = [
  'session',
  'status',
  'status_done',
  'text',
  'citations',
  'done',
  'error',
] as const;

export const DOCUMENT_CHAT_INVALID_STREAM_ERROR_CODE =
  'invalid_stream_message' as const;

const INVALID_STREAM_MESSAGE =
  'Received an invalid message from the assistant.';

const documentChatEventTypeSchema = z.object({
  type: z.string(),
});

const statusEventSchema = <T extends 'status' | 'status_done'>(type: T) =>
  z
    .object({
      type: z.literal(type),
      content: z.string().optional(),
      code: z.string().optional(),
    })
    .transform((event, ctx) => {
      const content = event.content?.trim() || event.code?.trim();
      if (!content) {
        ctx.addIssue({
          code: 'custom',
          message: 'Status event requires content or code',
        });
        return z.NEVER;
      }
      return { ...event, content };
    });

const documentChatEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('session'),
    session_id: z.string(),
  }),
  statusEventSchema('status'),
  statusEventSchema('status_done'),
  z.object({
    type: z.literal('text'),
    content: z.string(),
  }),
  z.object({
    type: z.literal('citations'),
    items: z.array(
      z.object({
        doc_id: z.string(),
        doc_name: z.string(),
        source_index: z.string(),
        labels: z.array(z.string()),
        excerpts: z.array(
          z.object({
            label: z.string(),
            chunk_id: z.string(),
            page: z.number().int().positive(),
            page_end: z.number().int().positive(),
            citation_preview: z.string().optional(),
            provenance: z
              .array(
                z.object({
                  page_no: z.number().int().positive(),
                  bbox: z
                    .tuple([z.number(), z.number(), z.number(), z.number()])
                    .optional(),
                  charspan: z.tuple([z.number(), z.number()]).optional(),
                  label: z.string().optional(),
                })
              )
              .default([]),
          })
        ),
      })
    ),
  }),
  z.object({
    type: z.literal('done'),
    session_id: z.string(),
  }),
  z.object({
    type: z.literal('error'),
    code: z.string(),
    message: z.string(),
  }),
]);

export type DocumentChatEvent = z.infer<typeof documentChatEventSchema>;

export type DocumentChatParseResult =
  | { outcome: 'event'; event: DocumentChatEvent }
  | { outcome: 'skip' }
  | { outcome: 'stream-error'; message: string };

async function httpErrorFromResponse(response: Response): Promise<HttpError> {
  const responseText = await response.text().catch(() => undefined);
  let errorBody: unknown;
  if (responseText) {
    try {
      errorBody = JSON.parse(responseText);
    } catch {
      errorBody = responseText;
    }
  }

  return new HttpError(extractErrorMessage(errorBody, response.statusText), {
    status: response.status,
    error: errorBody,
  } as HttpResponse<unknown, unknown>);
}

export type StreamPortalGatewayChatParams = {
  body: Record<string, unknown>;
  onEvent: (event: DocumentChatEvent) => void;
  onError: (error: Error) => void;
  onComplete: () => void;
  url: string;
};

export function parseDocumentChatEvent(
  data: string
): DocumentChatParseResult | null {
  if (data === '') {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    console.warn('Unknown DocumentChatEvent:', data);
    return { outcome: 'stream-error', message: INVALID_STREAM_MESSAGE };
  }

  const envelope = documentChatEventTypeSchema.safeParse(parsed);
  if (!envelope.success) {
    console.warn('Unknown DocumentChatEvent:', parsed);
    return { outcome: 'stream-error', message: INVALID_STREAM_MESSAGE };
  }

  if (
    !KNOWN_EVENT_TYPES.includes(
      envelope.data.type as (typeof KNOWN_EVENT_TYPES)[number]
    )
  ) {
    console.warn('Unknown DocumentChatEvent:', parsed);
    return { outcome: 'skip' };
  }

  const result = documentChatEventSchema.safeParse(parsed);
  if (result.success) {
    return { outcome: 'event', event: result.data };
  }

  // Status events are progress-only. A shape we do not recognize must not
  // abort the stream — main skipped unknown `status_done` for the same reason.
  if (envelope.data.type === 'status' || envelope.data.type === 'status_done') {
    console.warn('Unknown DocumentChatEvent:', parsed);
    return { outcome: 'skip' };
  }

  console.warn('Unknown DocumentChatEvent:', parsed);
  return { outcome: 'stream-error', message: INVALID_STREAM_MESSAGE };
}

export function streamPortalGatewayChat({
  body,
  onEvent,
  onError,
  onComplete,
  url,
}: StreamPortalGatewayChatParams): {
  abort: () => void;
} {
  let es: EventSourceClient;
  let finished = false;
  let terminalInlineErrorEmitted = false;

  const end = (outcome: 'complete' | Error) => {
    if (finished) {
      return;
    }
    finished = true;
    if (outcome === 'complete') {
      onComplete();
    } else {
      onError(outcome);
    }
  };

  const completeStream = () => {
    end('complete');
    es.close();
  };

  const finishWithInlineError = (message: string) => {
    terminalInlineErrorEmitted = true;
    onEvent({
      type: 'error',
      code: DOCUMENT_CHAT_INVALID_STREAM_ERROR_CODE,
      message,
    });
    completeStream();
  };

  const finishAfterUnexpectedClose = () => {
    if (terminalInlineErrorEmitted) {
      end('complete');
      return;
    }

    end(new Error('Document chat stream ended unexpectedly'));
    es.close();
  };

  const abortConnection = () =>
    new DOMException('The operation was aborted.', 'AbortError');

  // eventsource-client does not use portalGatewayClient; wrap fetch so auth headers
  // and session refresh apply before the SSE stream is consumed.
  const documentChatFetch: FetchLike = async (fetchUrl, init) => {
    try {
      const response = await fetchWithSessionRefresh(fetchUrl, init);
      if (!response.ok) {
        end(await httpErrorFromResponse(response));
        throw abortConnection();
      }
      return response;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      end(error instanceof Error ? error : new Error(String(error)));
      throw abortConnection();
    }
  };

  try {
    es = createEventSource({
      url,
      method: 'POST',
      fetch: documentChatFetch,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      onMessage: (message) => {
        if (finished) {
          return;
        }

        const parsed = parseDocumentChatEvent(message.data);
        if (parsed == null) {
          return;
        }

        if (parsed.outcome === 'skip') {
          return;
        }

        if (parsed.outcome === 'stream-error') {
          finishWithInlineError(parsed.message);
          return;
        }

        if (parsed.event.type === 'error') {
          terminalInlineErrorEmitted = true;
        }

        onEvent(parsed.event);

        if (parsed.event.type === 'done' || parsed.event.type === 'error') {
          completeStream();
        }
      },
      onDisconnect: () => {
        if (finished) {
          return;
        }

        finishAfterUnexpectedClose();
      },
      onScheduleReconnect: () => {
        if (finished) {
          return;
        }

        if (terminalInlineErrorEmitted) {
          end('complete');
          es.close();
          return;
        }

        end(new Error('Document chat connection lost'));
        es.close();
      },
    });
  } catch (error) {
    onError(error instanceof Error ? error : new Error(String(error)));
    return {
      abort: () => {
        // empty function
      },
    };
  }

  return {
    abort: () => {
      finished = true;
      es.close();
    },
  };
}
